/* eslint-disable no-param-reassign */
import { createAction, createReducer, createSelector } from '@reduxjs/toolkit';
import { merge } from 'lodash';
import hondaRestApi from '../apis/hondaRestApi';
import { OfferTypes } from '../types';
import { primaryCalculatorId } from './financialCalculators';
import { CUSTOMER_CASH_REBATE_NAME } from '../../utils/constants';
import { getStateFromAction } from '../utils';
export const offerPrimaryChanged = createAction('offers/offerPrimaryChanged');
export const offersPrimaryChanged = createAction('offers/offersPrimaryChanged');
export const loyaltyPrimaryChanged = createAction('offers/loyaltyPrimaryChanged');
export const resetOffers = createAction('offers/resetOffers');
/**
 * __ONLY__ used in the redux matchers for `hondaRestApi.endpoints.getOffers.matchFulfilled` in the following reducers:
 * 1. `offers`
 * 2. `financialCalculators`
 */
export const getDefaultIncentiveOfferIDs = ({ incentiveOffers, previousIncentiveOfferIds: previousIncentiveIds = [], skipNonStackableOffers = false, }) => {
    const defaultIncentiveOfferIds = incentiveOffers
        .filter(o => o.type === OfferTypes.StackableRebate)
        .map(offer => offer.id);
    const previousIncentiveIdsSet = new Set(previousIncentiveIds);
    const validPreviousIncentiveIds = incentiveOffers
        .filter(offer => previousIncentiveIdsSet.has(offer.id))
        .map(offer => offer.id);
    const optionallyDefaultIncentiveOfferIds = skipNonStackableOffers
        ? []
        : incentiveOffers
            .filter(o => o.type === OfferTypes.NonStackableRebate || o.name === CUSTOMER_CASH_REBATE_NAME)
            .map(offer => offer.id);
    return [
        ...new Set([...validPreviousIncentiveIds, ...defaultIncentiveOfferIds, ...optionallyDefaultIncentiveOfferIds]),
    ].filter(Boolean);
};
export const initialState = {};
const offers = createReducer(initialState, builder => {
    builder
        .addCase(offerPrimaryChanged, (state, action) => {
        var _a;
        const { calculatorId, offerId, transmissionKey, paymentMethod, isSelected } = action.payload;
        const transmissionState = (_a = state === null || state === void 0 ? void 0 : state[transmissionKey]) === null || _a === void 0 ? void 0 : _a[paymentMethod];
        if (calculatorId === primaryCalculatorId && transmissionState) {
            if (isSelected) {
                transmissionState.incentive = [...transmissionState.incentive, offerId];
            }
            else {
                transmissionState.incentive = transmissionState.incentive.filter(id => id !== offerId);
            }
        }
    })
        .addCase(offersPrimaryChanged, (state, action) => {
        var _a, _b, _c;
        var _d;
        const { calculatorId, offerIds, transmissionKey, paymentMethod, canCreateIfEmpty } = action.payload;
        if (canCreateIfEmpty && transmissionKey && paymentMethod) {
            (_a = state[transmissionKey]) !== null && _a !== void 0 ? _a : (state[transmissionKey] = { [paymentMethod]: { incentive: [], loyalty: '' } });
            (_b = (_d = state[transmissionKey])[paymentMethod]) !== null && _b !== void 0 ? _b : (_d[paymentMethod] = { incentive: [], loyalty: '' });
        }
        const transmissionState = (_c = state === null || state === void 0 ? void 0 : state[transmissionKey]) === null || _c === void 0 ? void 0 : _c[paymentMethod];
        if (calculatorId === primaryCalculatorId && transmissionState) {
            transmissionState.incentive = offerIds;
        }
    })
        .addCase(loyaltyPrimaryChanged, (state, action) => {
        var _a;
        const { calculatorId, offerKey, transmissionKey, paymentMethod } = action.payload;
        const transmissionState = (_a = state === null || state === void 0 ? void 0 : state[transmissionKey]) === null || _a === void 0 ? void 0 : _a[paymentMethod];
        if (calculatorId === primaryCalculatorId && transmissionState) {
            transmissionState.loyalty = offerKey;
        }
    })
        .addCase(resetOffers, () => initialState)
        .addMatcher(hondaRestApi.endpoints.getOffers.matchFulfilled, (state, action) => {
        const newState = action.payload.reduce((acc, item) => {
            if (!item.transmissionKey)
                return acc;
            acc[item.transmissionKey] = Object.entries(item.incentiveOffers).reduce((offersByPaymentMethod, [paymentMethod, incentiveOffers]) => {
                var _a, _b, _c, _d;
                if (!paymentMethod)
                    return offersByPaymentMethod;
                const previousPaymentMethodOffers = (_b = state[(_a = item.transmissionKey) !== null && _a !== void 0 ? _a : '']) === null || _b === void 0 ? void 0 : _b[paymentMethod];
                const selectedPaymentMethod = (_c = getStateFromAction(action, 'financialCalculators')[primaryCalculatorId]) === null || _c === void 0 ? void 0 : _c.paymentMethod;
                const skipNonStackableOffers = getStateFromAction(action, 'offerOptions').canSkipDefaultNonStackableOffers &&
                    paymentMethod === selectedPaymentMethod;
                offersByPaymentMethod[paymentMethod] = {
                    ...previousPaymentMethodOffers,
                    incentive: getDefaultIncentiveOfferIDs({
                        incentiveOffers,
                        previousIncentiveOfferIds: (_d = previousPaymentMethodOffers === null || previousPaymentMethodOffers === void 0 ? void 0 : previousPaymentMethodOffers.incentive) !== null && _d !== void 0 ? _d : [],
                        skipNonStackableOffers,
                    }),
                };
                return offersByPaymentMethod;
            }, {});
            return acc;
        }, {});
        merge(state, newState);
    })
        .addMatcher(hondaRestApi.endpoints.getOffersWithLoyalty.matchFulfilled, (state, action) => {
        var _a;
        const tmp = (_a = action.payload) === null || _a === void 0 ? void 0 : _a.reduce((acc, item) => {
            var _a;
            acc[item.transmissionKey] = (_a = Object.keys(item.loyaltyOffers)) === null || _a === void 0 ? void 0 : _a.reduce((method, key) => {
                if (item.loyaltyOffers[key].length === 1) {
                    method[key] = { loyalty: item.loyaltyOffers[key][0].key };
                }
                if (item.loyaltyOffers[key].length > 1) {
                    const loyalty = item.loyaltyOffers[key].find((offer) => offer.type === 'percent');
                    method[key] = { loyalty: loyalty === null || loyalty === void 0 ? void 0 : loyalty.key };
                }
                return method;
            }, {});
            return acc;
        }, {});
        return merge(state, tmp);
    });
});
export const selectSelectedIncentiveOffers = createSelector((state, transmissionKey, paymentMethod) => { var _a, _b; return (_b = (_a = state.offers) === null || _a === void 0 ? void 0 : _a[transmissionKey]) === null || _b === void 0 ? void 0 : _b[paymentMethod]; }, offersState => offersState === null || offersState === void 0 ? void 0 : offersState.incentive);
export const selectSelectedLoyaltyOffers = createSelector((state, transmissionKey, paymentMethod) => { var _a, _b; return (_b = (_a = state.offers) === null || _a === void 0 ? void 0 : _a[transmissionKey]) === null || _b === void 0 ? void 0 : _b[paymentMethod]; }, offersState => offersState === null || offersState === void 0 ? void 0 : offersState.loyalty);
export const selectSelectedOffers = createSelector((state, transmissionKey, paymentMethod) => { var _a, _b; return (_b = (_a = state.offers) === null || _a === void 0 ? void 0 : _a[transmissionKey]) === null || _b === void 0 ? void 0 : _b[paymentMethod]; }, offersState => ({
    incentive: offersState === null || offersState === void 0 ? void 0 : offersState.incentive,
    loyalty: offersState === null || offersState === void 0 ? void 0 : offersState.loyalty,
}));
export default offers;
